import React, { useState, useEffect } from 'react';
import { useLocation } from '../contexts/LocationContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  Paper,
  Avatar,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconMapPin,
  IconSearch,
  IconCheck,
  IconX,
  IconBuilding,
  IconPhone,
  IconMail,
  IconUser
} from '@tabler/icons-react';

const LocationManagement: React.FC = () => {
  const locationContext = useLocation();
  const { locations, loading, error, fetchLocations, createLocation, updateLocation, deleteLocation } = locationContext;
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    country: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    isActive: true
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const getStatusColor = (isActive: boolean) => {
    return isActive ? '#4CAF50' : '#F44336';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Aktif' : 'Pasif';
  };

  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (location.address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (location.city || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && location.isActive) ||
                         (statusFilter === 'inactive' && !location.isActive);
    return matchesSearch && matchesStatus;
  });

  const handleAddLocation = () => {
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      country: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      isActive: true
    });
    setOpenDialog(true);
  };

  const handleEditLocation = (location: any) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      city: location.city || '',
      country: location.country || '',
      contactPerson: location.contactPerson || '',
      contactEmail: location.contactEmail || '',
      contactPhone: location.contactPhone || '',
      isActive: location.isActive
    });
    setOpenDialog(true);
  };

  const handleSaveLocation = async () => {
    try {
      if (editingLocation) {
        await updateLocation(editingLocation._id, formData);
      } else {
        await createLocation(formData);
      }
      setOpenDialog(false);
      setFormData({
        name: '',
        address: '',
        city: '',
        country: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        isActive: true
      });
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const handleDeleteLocation = async (locationId: string) => {
    if (window.confirm('Bu lokasyonu silmek istediğinizden emin misiniz?')) {
      try {
        await deleteLocation(locationId);
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    }
  };

  const handleToggleStatus = async (locationId: string, currentStatus: boolean) => {
    try {
      await updateLocation(locationId, { isActive: !currentStatus });
    } catch (error) {
      console.error('Error updating location status:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Lokasyonlar yükleniyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="700" color="white" sx={{ fontFamily: 'Monaco, monospace', mb: 1, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            Lokasyon Yönetimi
          </Typography>
          <Typography variant="body1" color="white" sx={{ fontFamily: 'Monaco, monospace', opacity: 0.9, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            Şirket lokasyonlarını yönetin ve organize edin
          </Typography>
        </Box>

        {/* İstatistikler */}
        <Grid2 container spacing={3} sx={{ mb: 3 }}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {locations.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Toplam Lokasyon
                    </Typography>
                  </Box>
                  <IconMapPin size={40} color="#4CAF50" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {locations.filter(l => l.isActive).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Aktif Lokasyon
                    </Typography>
                  </Box>
                  <IconCheck size={40} color="#2196F3" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {locations.filter(l => !l.isActive).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Pasif Lokasyon
                    </Typography>
                  </Box>
                  <IconX size={40} color="#FF9800" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {locations.filter(l => l.contactPerson).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      İletişim Kişisi
                    </Typography>
                  </Box>
                  <IconUser size={40} color="#9C27B0" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Ana İçerik */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            {/* Üst Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Lokasyon ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <IconSearch size={20} color="#666" style={{ marginRight: 8 }} />
                  }}
                  sx={{ 
                    minWidth: 250,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Monaco, monospace'
                    }
                  }}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<IconUserPlus size={20} />}
                onClick={handleAddLocation}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontFamily: 'Monaco, monospace',
                  fontWeight: 600,
                px: 3,
                py: 1
              }}
            >
              Yeni Lokasyon
            </Button>
          </Box>

          {/* Tablo */}
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#F5F5F5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Lokasyon Adı</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Adres</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Şehir</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>İletişim</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLocations.map((location) => (
                  <TableRow key={location._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          <IconBuilding size={18} color="white" />
                        </Avatar>
                        <Typography variant="body2" fontWeight="500" sx={{ fontFamily: 'Monaco, monospace' }}>
                          {location.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                        {location.address || 'Belirtilmemiş'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                        {location.city || 'Belirtilmemiş'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {location.contactPerson && (
                          <Typography variant="caption" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                            <IconUser size={12} color="#666" style={{ marginRight: 4 }} />
                            {location.contactPerson}
                          </Typography>
                        )}
                        {location.contactEmail && (
                          <Typography variant="caption" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                            <IconMail size={12} color="#666" style={{ marginRight: 4 }} />
                            {location.contactEmail}
                          </Typography>
                        )}
                        {location.contactPhone && (
                          <Typography variant="caption" color="#666" sx={{ fontFamily: 'Monaco, monospace' }}>
                            <IconPhone size={12} color="#666" style={{ marginRight: 4 }} />
                            {location.contactPhone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(location.isActive)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(location.isActive) + '20',
                          color: getStatusColor(location.isActive),
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleEditLocation(location)}
                            sx={{ color: 'primary.main' }}
                          >
                            <IconEdit size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={location.isActive ? 'Pasifleştir' : 'Aktifleştir'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(location._id, location.isActive)}
                            sx={{ color: location.isActive ? 'warning.main' : 'success.main' }}
                          >
                            <Switch
                              checked={location.isActive}
                              size="small"
                              sx={{ '& .MuiSwitch-thumb': { width: 16, height: 16 } }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteLocation(location._id)}
                            sx={{ color: 'error.main' }}
                          >
                            <IconTrash size={20} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          {editingLocation ? 'Lokasyon Düzenle' : 'Yeni Lokasyon Ekle'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Lokasyon Adı"
              placeholder="Merkez Ofis"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Adres"
              placeholder="Tam adres bilgisi..."
              multiline
              rows={2}
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Şehir"
                placeholder="İstanbul"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                sx={{ fontFamily: 'Inter, sans-serif' }}
              />
              <TextField
                fullWidth
                label="Ülke"
                placeholder="Türkiye"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                sx={{ fontFamily: 'Inter, sans-serif' }}
              />
            </Box>
            <TextField
              fullWidth
              label="İletişim Kişisi"
              placeholder="Ad Soyad"
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="E-posta"
                type="email"
                placeholder="ornek@email.com"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                sx={{ fontFamily: 'Inter, sans-serif' }}
              />
              <TextField
                fullWidth
                label="Telefon"
                placeholder="+90 212 123 45 67"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                sx={{ fontFamily: 'Inter, sans-serif' }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveLocation}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              px: 3
            }}
          >
            {editingLocation ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default LocationManagement;

